import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
    Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, Icon, IconButton, InputLabel,
    MenuItem, Switch, Table, TableBody, TableCell, TableContainer, TableRow, Tooltip,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import DataTableHeadCell from "examples/Tables/DataTable/DataTableHeadCell";
import { get, post } from "utils/WebService";

const rotulo = { px: 1, pb: 0.5, pt: 0.2 };
const VAZIO = { id: null, nome: "", acao: "MANUAL", orientacao: "", exigeDebitos: false, exigeAnexo: false, ativo: true, ordem: 100, idTipoOcorrencia: "" };

/**
 * Lista de tipos de solicitação ao suporte (Fila de Solicitações, quem tem c067_U): o nome que o usuário escolhe, o que
 * a aprovação faz (a ação), a orientação mostrada a quem pede, se pede débitos ou anexo, a ocorrência do pedido na ficha
 * e a ordem. Tipo já usado não se exclui: inative.
 */
export default function TiposSolicitacaoDialog({ open, onClose, onAlterado }) {
    const [tipos, setTipos] = useState([]);
    const [acoes, setAcoes] = useState([]);
    const [ocorrencias, setOcorrencias] = useState([]);
    const [editando, setEditando] = useState(null);
    const [carregando, setCarregando] = useState(false);
    const [salvando, setSalvando] = useState(false);
    const [erro, setErro] = useState("");

    useEffect(() => {
        if (!open) return;
        setEditando(null);
        setErro("");
        setCarregando(true);
        get("/solicitacoes-suporte/tipos?todos=true")
            .then(res => { setTipos(res?.tipos || []); setAcoes(res?.acoes || []); })
            .catch(e => setErro(e.message || "Erro ao carregar os tipos."))
            .finally(() => setCarregando(false));
        get("/tipo-ocorrencia/list-all").then(res => setOcorrencias(res?.list || res?.data || (Array.isArray(res) ? res : []))).catch(() => setOcorrencias([]));
    }, [open]);

    const aplicar = (res) => {
        setTipos(res?.tipos || []);
        setEditando(null);
        if (onAlterado) onAlterado();
    };

    const salvar = () => {
        if (!editando.nome.trim()) { setErro("Informe o nome."); return; }
        setSalvando(true);
        setErro("");
        post("/solicitacoes-suporte/tipos/salvar", { ...editando, idTipoOcorrencia: editando.idTipoOcorrencia || null })
            .then(aplicar)
            .catch(e => setErro(e.message || "Erro ao salvar."))
            .finally(() => setSalvando(false));
    };

    const excluir = (t) => {
        if (!window.confirm(`Excluir o tipo "${t.nome}"?`)) return;
        setErro("");
        post(`/solicitacoes-suporte/tipos/${t.id}/excluir`, {})
            .then(aplicar)
            .catch(e => setErro(e.message || "Erro ao excluir."));
    };

    const muda = (campo) => (e) => setEditando({ ...editando, [campo]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle>Tipos de solicitação ao suporte</DialogTitle>
            <DialogContent>
                <MDTypography variant="caption" color="text" display="block" mb={1.5}>
                    A lista que o usuário vê em Cobrança &gt; Solicitações ao Suporte. A ação diz o que acontece ao aprovar: devolução e
                    reativação o sistema faz; "o suporte faz e conclui" você faz na tela certa (Baixa de Pagamento, contrato...) e conclui
                    com a resposta para quem pediu. A migração continua sendo pedida pela ficha.
                </MDTypography>
                {carregando ? (
                    <MDBox display="flex" justifyContent="center" py={3}><CircularProgress size={26} color="info" /></MDBox>
                ) : (
                    <TableContainer sx={{ boxShadow: "none" }}>
                        <Table size="small">
                            <MDBox component="thead">
                                <TableRow>
                                    <DataTableHeadCell width="22%" sorted={false}>Nome</DataTableHeadCell>
                                    <DataTableHeadCell width="24%" sorted={false}>Ao aprovar</DataTableHeadCell>
                                    <DataTableHeadCell width="22%" sorted={false}>Pede</DataTableHeadCell>
                                    <DataTableHeadCell width="8%" sorted={false}>Ordem</DataTableHeadCell>
                                    <DataTableHeadCell width="10%" sorted={false}>Usado</DataTableHeadCell>
                                    <DataTableHeadCell width="14%" sorted={false} align="center">Ações</DataTableHeadCell>
                                </TableRow>
                            </MDBox>
                            <TableBody>
                                {tipos.map(t => (
                                    <TableRow key={t.id} sx={{ opacity: t.ativo ? 1 : 0.55 }}>
                                        <TableCell sx={{ verticalAlign: "top", pt: 1.5 }}>
                                            <MDTypography variant="caption" fontWeight="bold" color="text" display="block">{t.nome}{t.ativo ? "" : " (inativo)"}</MDTypography>
                                            {t.orientacao && <MDTypography variant="caption" color="text" sx={{ opacity: 0.85 }}>{t.orientacao}</MDTypography>}
                                        </TableCell>
                                        <TableCell sx={{ verticalAlign: "top", pt: 1.5 }}>
                                            <MDTypography variant="caption" color="text">{t.acaoLabel}</MDTypography>
                                        </TableCell>
                                        <TableCell sx={{ verticalAlign: "top", pt: 1.5 }}>
                                            <MDTypography variant="caption" color="text">
                                                {[t.exigeDebitos && "débitos", t.exigeAnexo && "anexo"].filter(Boolean).join(" e ") || "-"}
                                                {t.nomeTipoOcorrencia ? ` · ocorrência ${t.nomeTipoOcorrencia}` : ""}
                                            </MDTypography>
                                        </TableCell>
                                        <TableCell sx={{ verticalAlign: "top", pt: 1.5 }}><MDTypography variant="caption" color="text">{t.ordem}</MDTypography></TableCell>
                                        <TableCell sx={{ verticalAlign: "top", pt: 1.5 }}><MDTypography variant="caption" color="text">{t.usos || 0}</MDTypography></TableCell>
                                        <TableCell align="center" sx={{ verticalAlign: "top", pt: 1 }}>
                                            <Tooltip title="Editar">
                                                <IconButton size="small" color="info" onClick={() => setEditando({ ...VAZIO, ...t, orientacao: t.orientacao || "", idTipoOcorrencia: t.idTipoOcorrencia || "" })}>
                                                    <Icon>edit</Icon>
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title={t.usos ? "Já usado: inative em vez de excluir" : "Excluir"}>
                                                <span>
                                                    <IconButton size="small" color="error" disabled={!!t.usos} onClick={() => excluir(t)}><Icon>delete</Icon></IconButton>
                                                </span>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                {editando && (
                    <MDBox mt={2} p={1.5} sx={{ border: "1px solid", borderColor: "divider", borderRadius: "8px" }}>
                        <MDTypography variant="button" fontWeight="bold" display="block" mb={1}>{editando.id ? "Editar tipo" : "Novo tipo"}</MDTypography>
                        <Grid container spacing={1.5}>
                            <Grid item xs={12} md={4}>
                                <InputLabel sx={rotulo}>Nome *</InputLabel>
                                <MDInput fullWidth size="small" value={editando.nome} onChange={muda("nome")} inputProps={{ maxLength: 100 }} />
                            </Grid>
                            <Grid item xs={12} md={5}>
                                <InputLabel sx={rotulo}>Ao aprovar *</InputLabel>
                                <MDInput select fullWidth size="small" value={editando.acao} onChange={muda("acao")}>
                                    {acoes.map(a => <MenuItem key={a.value} value={a.value}>{a.label}</MenuItem>)}
                                </MDInput>
                            </Grid>
                            <Grid item xs={6} md={1.5}>
                                <InputLabel sx={rotulo}>Ordem</InputLabel>
                                <MDInput fullWidth size="small" type="number" value={editando.ordem} onChange={muda("ordem")} />
                            </Grid>
                            <Grid item xs={6} md={1.5}>
                                <FormControlLabel sx={{ mt: 2.5, ml: 0 }} control={<Switch checked={!!editando.ativo} onChange={muda("ativo")} />}
                                    label={<MDTypography variant="caption" color="text" ml={0.5}>Ativo</MDTypography>} />
                            </Grid>
                            <Grid item xs={12} md={8}>
                                <InputLabel sx={rotulo}>Orientação para quem pede</InputLabel>
                                <MDInput fullWidth size="small" multiline rows={2} value={editando.orientacao} onChange={muda("orientacao")} inputProps={{ maxLength: 1000 }} />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <InputLabel sx={rotulo}>Ocorrência do pedido na ficha</InputLabel>
                                <MDInput select fullWidth size="small" value={editando.idTipoOcorrencia} onChange={muda("idTipoOcorrencia")} SelectProps={{ displayEmpty: true }}>
                                    <MenuItem value="">A padrão da ação</MenuItem>
                                    {ocorrencias.map(o => <MenuItem key={o.id} value={o.id}>{o.nome}</MenuItem>)}
                                </MDInput>
                            </Grid>
                            <Grid item xs={12} display="flex" gap={2} flexWrap="wrap">
                                <FormControlLabel sx={{ ml: 0 }} control={<Checkbox checked={!!editando.exigeDebitos} onChange={muda("exigeDebitos")} />}
                                    label={<MDTypography variant="caption" color="text">Pede os débitos</MDTypography>} />
                                <FormControlLabel sx={{ ml: 0 }} control={<Checkbox checked={!!editando.exigeAnexo} onChange={muda("exigeAnexo")} />}
                                    label={<MDTypography variant="caption" color="text">Pede anexo (ex.: comprovante)</MDTypography>} />
                            </Grid>
                        </Grid>
                        <MDBox display="flex" justifyContent="flex-end" gap={1} mt={1}>
                            <MDButton variant="text" color="secondary" size="small" onClick={() => setEditando(null)}>Cancelar</MDButton>
                            <MDButton variant="gradient" color="info" size="small" onClick={salvar} disabled={salvando}>
                                {salvando ? <CircularProgress size={16} color="inherit" /> : "Salvar tipo"}
                            </MDButton>
                        </MDBox>
                    </MDBox>
                )}
                {erro && <MDTypography variant="caption" color="error" display="block" mt={1}>{erro}</MDTypography>}
            </DialogContent>
            <DialogActions>
                {!editando && (
                    <MDButton variant="outlined" color="info" onClick={() => setEditando({ ...VAZIO })}>
                        <Icon sx={{ mr: 0.5 }}>add</Icon>Novo tipo
                    </MDButton>
                )}
                <MDButton variant="text" color="secondary" onClick={onClose}>Fechar</MDButton>
            </DialogActions>
        </Dialog>
    );
}

TiposSolicitacaoDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onAlterado: PropTypes.func,
};

TiposSolicitacaoDialog.defaultProps = {
    onAlterado: null,
};
